import { useCallback, useMemo, useState } from "react"

import { Button, Popover } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import { formatMinutesAsLabel, getSlaSuggestionsForInput } from "@/lib/dates"

const PRESET_DURATIONS = [
	{ label: "1 hour", minutes: 60 },
	{ label: "2 hours", minutes: 120 },
	{ label: "4 hours", minutes: 240 },
	{ label: "8 hours", minutes: 480 },
	{ label: "12 hours", minutes: 720 },
	{ label: "24 hours", minutes: 1440 },
	{ label: "48 hours", minutes: 2880 },
	{ label: "1 week", minutes: 10080 },
	{ label: "2 weeks", minutes: 20160 },
]

interface SlaTimePickerProps {
	value: number | null
	onChange: (minutes: number | null) => void
	disabled?: boolean
	className?: string
}

export function SlaTimePicker({
	value,
	onChange,
	disabled,
	className,
}: SlaTimePickerProps) {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState("")

	const suggestions = useMemo(() => getSlaSuggestionsForInput(query), [query])

	const handleSelect = useCallback(
		(minutes: number | null) => {
			onChange(minutes)
			setQuery("")
			setOpen(false)
		},
		[onChange],
	)

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			className="p-0"
			content={
				<Command shouldFilter={false}>
					<CommandInput
						autoFocus
						placeholder="Type a duration..."
						value={query}
						onValueChange={setQuery}
					/>
					<CommandEmpty>No matching option</CommandEmpty>
					<CommandList>
						<CommandGroup>
							{!query && (
								<CommandItem onSelect={() => handleSelect(null)}>
									None
								</CommandItem>
							)}
							{suggestions.length === 0
								? PRESET_DURATIONS.map((opt) => (
										<CommandItem
											key={opt.minutes}
											onSelect={() => handleSelect(opt.minutes)}
										>
											{opt.label}
										</CommandItem>
									))
								: suggestions.map((suggestion) => (
										<CommandItem
											key={suggestion.label}
											value={suggestion.label}
											onSelect={() => handleSelect(suggestion.minutes)}
										>
											{suggestion.label}
										</CommandItem>
									))}
						</CommandGroup>
					</CommandList>
				</Command>
			}
		>
			<Button
				variant="outline"
				disabled={disabled}
				className={className ?? "w-[130px] justify-start text-sm"}
			>
				{formatMinutesAsLabel(value)}
			</Button>
		</Popover>
	)
}
